import { Injectable, inject } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { Router } from '@angular/router'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Action, Store, select } from '@ngrx/store'
import { analyzereConstants } from '@shared/constants/analyzere'
import { LoadingPopupComponent } from '@shared/loading-popup/loading-popup.component'
import { MessageDialogService } from '@shared/message-dialog.service'
import {
  EMPTY,
  Observable,
  concat,
  defer,
  forkJoin,
  from,
  lastValueFrom,
  merge,
  of,
  throwError,
} from 'rxjs'
import {
  catchError,
  concatMap,
  debounceTime,
  defaultIfEmpty,
  delay,
  filter,
  finalize,
  map,
  mergeMap,
  switchMap,
  take,
  toArray,
  withLatestFrom,
} from 'rxjs/operators'
import { isMultiSectionLayer } from 'src/app/analysis/layers/multi-section-layer'
import { isSlidingScaleLayer } from 'src/app/analysis/layers/sliding-scale-layer'
import { layerIds } from 'src/app/analysis/model/layer-palette.model'
import { Layer, LayerRef, NestedLayer, WithLayerId } from 'src/app/analysis/model/layers.model'
import {
  isLayerActualTopAndDrop,
  isLayerAgg,
  isLayerAggFeeder,
  isLayerFHCF,
  isLayerShared,
} from 'src/app/analysis/model/layers.util'
import { deleteBulkItemComplete, openCloneDialog } from 'src/app/analysis/store/analysis.actions'
import {
  selectCurrentClientID,
  selectCurrentProgram,
  selectCurrentProgramParentGrossScenarios,
  selectCurrentStudyID,
  selectCurrentYearID,
} from 'src/app/analysis/store/analysis.selectors'
import { BackendService } from 'src/app/api/backend/backend.service'
import { InuranceService } from 'src/app/api/inurance/inurance.service'
import { MigrationFlagService } from 'src/app/api/migration-flag.service'
import { errorPayload } from 'src/app/error/model/error'
import { ConfirmationDialogService } from 'src/app/shared/services/confirmation-dialog.service'
import {
  CloneDialogComponent,
  CloneDialogResult,
} from 'src/app/tier/clone-dialog/clone-dialog.component'
import { SwapLossSetsDialogComponent } from 'src/app/tier/swap-loss-sets-dialog/swap-loss-sets-dialog.component'
import { buildTierRouteCommands } from 'src/app/tier/tier.util'
import { ReturnPeriodRow } from '../../../analysis/model/metrics.model'
import {
  LogicalPortfolioLayer,
  LossSet,
  LossSetLayer,
  Metadata,
  PhysicalPortfolioLayer,
  Ref,
  Update,
} from '../../../api/analyzere/analyzere.model'
import { AnalyzreService } from '../../../api/analyzere/analyzre.service'
import { ProgramService } from '../../../api/program/program.service'
import { mergeMapWithInput, rejectError, rejectErrorWithInput } from '../../../api/util'
import { Program } from '../../model/program.model'
import { selectCurrentStructureID } from '../broker/broker.selectors'
import { AppState } from '../index'
import {
  deleteAllLossSetScenarios,
  deleteAllLossSetScenariosNetFailure,
  deleteAllLossSetScenariosNetSuccess,
  deleteBulkScenarios,
  deleteScenario,
  deleteScenarioFailure,
  deleteScenarioNetComplete,
  deleteScenarioNetSuccess,
  deleteScenarioSuccess,
  handleSwapBulkLossSets,
  importBulkLossSets,
  importBulkLossSetsSuccess,
  postParentGrossMappingLabels,
  postParentGrossMappingLabelsFailure,
  postParentGrossMappingLabelsSuccess,
  saveTowerPreferences,
  saveTowerPreferenesFailure,
  saveTowerPreferenesSuccess,
  setProgramNameAndDescription,
  setProgramNameAndDescriptionFailure,
  setProgramNameAndDescriptionSuccess,
  swapBulkLossSets,
  swapBulkLossSetsIntermediateSuccess,
  swapBulkLossSetsSuccess,
  updateCounts,
  updateCountsFailure,
  updateCountsSuccess,
  updateFolderID,
  updateFolderIDFailure,
  updateFolderIDSuccess,
  updateParentGrossMappingLabels,
  updateParentGrossMappingLabelsFailure,
  updateParentGrossMappingLabelsSuccess,
  updateProgramIndex,
  updateProgramIndexFailure,
  updateProgramIndexSuccess,
  updateProgramIndexes,
  updateProgramIndexesFailure,
  updateProgramIndexesSuccess,
  updateTailMetrics,
  updateTailMetricsFailure,
  updateTailMetricsSuccess,
} from './program.actions'
import {
  fromDeleteLossSetScenariosResponse,
  toDeleteAllLossSetScenariosRequest,
} from './program.mappers'
import { selectCurrentStudyPrograms } from './program.selectors'

@Injectable()
export class ProgramEffects {
  private actions$ = inject(Actions)
  private programService = inject(ProgramService)
  private store = inject<Store<AppState>>(Store)
  private service = inject(AnalyzreService)
  private dialog = inject(MatDialog)
  private messageService = inject(MessageDialogService)
  private snackBar = inject(MatSnackBar)
  private backendService = inject(BackendService)
  private router = inject(Router)
  private confirmationDialogService = inject(ConfirmationDialogService)
  private inuranceService = inject(InuranceService)
  private migrationFlags = inject(MigrationFlagService)

  setTowerPreferences$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(saveTowerPreferences),
      switchMap((action) => {
        return this.programService.setTowerPreferences(action.id, action.preferences)
      }),
      switchMap((res) => {
        const actions = []
        if (res.error) {
          actions.push(saveTowerPreferenesFailure({ error: res.error }))
        } else if (res.data !== undefined) {
          actions.push(saveTowerPreferenesSuccess(res.data))
        }
        return actions
      }),
    )
  })

  setProgramNameAndDescription$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(setProgramNameAndDescription),
      mergeMap(({ structure, name, description }) =>
        this.programService.update(structure.id, {
          ...structure,
          label: name,
          description,
        }),
      ),
      rejectError((error) => this.store.dispatch(setProgramNameAndDescriptionFailure({ error }))),
      filter(({ label }) => label != null && label.length > 0),
      map(({ id, label, description }) =>
        setProgramNameAndDescriptionSuccess({ id, name: label, description }),
      ),
    )
  })

  handleSwapBulkLossSets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(handleSwapBulkLossSets),
      concatMap(({ structures, analysisProfileID, newParentGrossPortfolioID, lossSetMapping }) =>
        from(structures).pipe(
          concatMap((structure, index) => {
            const isLast = index === structures.length - 1
            const oldParentGrossPortfolioID = structure.parentGrossPortfolioID ?? ''
            this.store.dispatch(
              swapBulkLossSets({
                structure,
                analysisProfileID,
                oldParentGrossPortfolioID,
                newParentGrossPortfolioID,
                lossSetMapping,
                isLast,
              }),
            )
            return this.actions$.pipe(
              ofType(swapBulkLossSetsSuccess, swapBulkLossSetsIntermediateSuccess),
              filter((action) => action.id === structure.id),
              take(1),
              map(() => ({
                type: '[Program Properties] Intermediate Processing',
              })),
            )
          }),
        ),
      ),
    ),
  )

  openCloneDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(openCloneDialog),
      mergeMap(({ title, selectedStructures, allStructures }) => {
        if (!selectedStructures?.length) {
          return EMPTY
        }

        return from(this.checkForSharedLimitLayers(selectedStructures, allStructures)).pipe(
          switchMap((sharedLimitErrorMessageElements) => {
            return this.dialog
              .open(CloneDialogComponent, {
                data: {
                  title,
                  sharedLimitErrorMessageElements,
                },
              })
              .afterClosed()
          }),
          filter((result) => result?.event === 'save'),
          mergeMap((result: CloneDialogResult) => {
            if (title === 'Add/Replace Loss Sets') {
              return this.handleAddLossSets(selectedStructures, result)
            } else if (title === 'Swap Loss Sets') {
              return this.handleSwapLossSets(selectedStructures, result)
            }
            return EMPTY
          }),
          catchError((error) => {
            this.messageService.showMessage('Error handling clone dialog', error)
            return EMPTY
          }),
        )
      }),
    ),
  )

  swapBulkLossSets$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(swapBulkLossSets),
      mergeMap(
        ({
          structure,
          oldParentGrossPortfolioID,
          newParentGrossPortfolioID,
          analysisProfileID,
          lossSetMapping,
          isLast,
        }) =>
          this.programService
            .update(structure.id, {
              ...structure,
              libRE: '',
              parentGrossPortfolioID: newParentGrossPortfolioID,
              analysisID: analysisProfileID,
            })
            .pipe(
              switchMap(() => {
                return forkJoin([
                  this.service.fetchPortfolio(oldParentGrossPortfolioID),
                  this.service.fetchPortfolio(newParentGrossPortfolioID),
                  this.service.fetchPortfolio(structure.cededPortfolioID),
                ]).pipe(
                  map(([oldLossSetLayers, newLossSetLayers, cededLayers]: [any, any, any]) => ({
                    structure,
                    oldParentGrossPortfolioID,
                    oldLossSetLayers,
                    newLossSetLayers,
                    cededLayers,
                    lossSetMapping,
                    isLast,
                    analysisProfileID,
                  })),
                )
              }),
            ),
      ),
      switchMap(
        ({
          structure,
          oldParentGrossPortfolioID,
          newLossSetLayers,
          cededLayers,
          lossSetMapping,
          isLast,
          analysisProfileID,
        }) => {
          const lossSets = new Set<LossSetLayer>()
          lossSetMapping.forEach((_: LossSetLayer[], key: LossSetLayer) => {
            lossSets.add(key)
          })
          return this.handleSharedLimitLayers(
            cededLayers,
            Array.from(lossSets),
            analysisProfileID,
            this.service,
          ).pipe(
            map((slData: any) => ({
              structure,
              oldParentGrossPortfolioID,
              newLossSetLayers,
              cededLayers,
              lossSetMapping,
              isLast,
              slData,
              analysisProfileID,
            })),
            defaultIfEmpty({
              structure,
              oldParentGrossPortfolioID,
              newLossSetLayers,
              cededLayers,
              lossSetMapping,
              isLast,
              slData: {},
              analysisProfileID,
            }),
          )
        },
      ),
      concatMap(
        ({
          structure,
          oldParentGrossPortfolioID,
          newLossSetLayers,
          cededLayers,
          lossSetMapping,
          isLast,
          slData,
          analysisProfileID,
        }) => {
          const grossLayerSet = new Set<string>()
          lossSetMapping.forEach((_: LossSetLayer[], key: LossSetLayer) => {
            grossLayerSet.add(key.id)
          })

          const grossLayers = Array.from(grossLayerSet)

          const netLayers: string[] = [...grossLayers]
          cededLayers.data?.layers.forEach((l: { id: string }) => {
            netLayers.push(l.id)
          })
          const backallocatedUpdates: Update<NestedLayer & WithLayerId>[] = []
          const complexSourceUpdates: Update<LogicalPortfolioLayer>[] = []
          const cededLayersTable = cededLayers.data?.layers
          const condensedMap = new Map<string, Set<string>>()
          const condensedMapRisk = new Map<string, Set<string>>()
          const condensedMapComplex = new Map<string, Set<string>>()
          let mappedLayerSetRisk: Set<string> | undefined
          let mappedLayerSetShared: Set<string> | undefined

          cededLayersTable.forEach((layer: any) => {
            condensedMap.set(layer.id, new Set<string>())
            let mappedLayerSet = condensedMap.get(layer.id)
            layer.sources.forEach((source: any) => {
              const sourceAndMetaData = !!source && !!source.meta_data

              const isRiskLayer =
                sourceAndMetaData &&
                (source.meta_data as Metadata).sage_layer_type === layerIds.noncatRisk

              const isSharedLimitLayer = sourceAndMetaData && isLayerShared(source)

              const isComplexLayer =
                sourceAndMetaData &&
                (source.meta_data.sage_layer_type === layerIds.noncatSwing ||
                  source.meta_data.sage_layer_type === layerIds.ahlSwing ||
                  source.meta_data.sage_layer_type === layerIds.noncatIndxl ||
                  isMultiSectionLayer(layer) ||
                  isSlidingScaleLayer(layer))

              const isTopAndDrop =
                sourceAndMetaData &&
                isLayerActualTopAndDrop(layer) &&
                (source.meta_data.sage_layer_type === layerIds.drop ||
                  source.meta_data.sage_layer_subtype === 'virtual')

              const complexSourceUpdateIDs: string[] = []
              if (isRiskLayer) {
                condensedMapRisk.set(source.id, new Set<string>())
                mappedLayerSetRisk = condensedMapRisk.get(source.id)
              }
              if (isSharedLimitLayer) {
                const backallocated = slData.backallocatedLayers.find(
                  (bal: any) =>
                    bal.data.meta_data.backAllocatedForID === layer.meta_data.backAllocatedForID,
                ).data
                const ogLayers = backallocated.sink.sources
                ogLayers.forEach((og: any) => {
                  og.sources.forEach((ogSource: any) => {
                    complexSourceUpdateIDs.push(ogSource.id)
                  })
                })
                complexSourceUpdates.push({
                  id: layer.id,
                  change: {
                    sources: [{ ref_id: backallocated.id }],
                  },
                })
                backallocatedUpdates.push({
                  id: backallocated.sink.id,
                  change: {
                    meta_data: {
                      ...backallocated.sink.meta_data,
                      analysisProfileID,
                    },
                  },
                })
              }
              if (isComplexLayer) {
                const subTypes = [
                  'loss-layer',
                  'adjustment-layer',
                  'premium-layer',
                  'section-layer',
                ]
                const subTypeIncluded = subTypes.includes(source.meta_data.sage_layer_subtype)
                const isIndex = layer.meta_data.sage_layer_type === layerIds.noncatIndxl
                if (subTypeIncluded || isIndex) {
                  complexSourceUpdateIDs.push(source.id)
                  if (
                    (isIndex || isMultiSectionLayer(layer)) &&
                    layer.meta_data.visible_layer_id &&
                    !complexSourceUpdates
                      .map((update) => update.id)
                      .includes(layer.meta_data.visible_layer_id)
                  ) {
                    complexSourceUpdateIDs.push(layer.meta_data.visible_layer_id)
                  }
                }
              }
              if (isLayerAgg(layer) || isLayerAggFeeder(layer)) {
                complexSourceUpdateIDs.push(source.id)
              }
              if (isTopAndDrop) {
                complexSourceUpdateIDs.push(source.id)
                if (source.meta_data.topID) {
                  complexSourceUpdateIDs.push(source.meta_data.topID)
                }
              }
              complexSourceUpdateIDs.forEach((id) => {
                const sources = newLossSetLayers.data.layers.map((lsl: any) => ({ ref_id: lsl.id }))
                complexSourceUpdates.push({
                  id: id,
                  change: {
                    sources,
                  },
                })
              })
              lossSetMapping.forEach((lossSet: LossSetLayer[], key: LossSetLayer) => {
                if (isRiskLayer) {
                  if (
                    source.loss_sets &&
                    source.loss_sets[0] &&
                    lossSet.find(
                      (l: LossSetLayer) =>
                        (l.loss_sets[0] as LayerRef).id === source.loss_sets[0].id,
                    )
                  ) {
                    mappedLayerSetRisk?.add((key.loss_sets[0] as LayerRef).id)
                  }
                } else if (isSharedLimitLayer) {
                  if (
                    source.loss_sets &&
                    source.loss_sets[0] &&
                    lossSet.find(
                      (l: LossSetLayer) =>
                        (l.loss_sets[0] as LayerRef).id === source.loss_sets[0].id,
                    )
                  ) {
                    mappedLayerSetShared?.add((key.loss_sets[0] as LayerRef).id)
                  }
                } else {
                  if (lossSet.find((l) => l.id === source.id)) {
                    mappedLayerSet?.add(key.id)
                  }
                }
              })
            })
          })
          const updates: Update<LogicalPortfolioLayer>[] = [...complexSourceUpdates]
          const riskUpdates: Update<PhysicalPortfolioLayer>[] = []
          const sharedUpdates: Update<PhysicalPortfolioLayer>[] = []
          const complexUpdates: Update<PhysicalPortfolioLayer>[] = []
          condensedMap.forEach((set, id) => {
            if (set && set.size > 0) {
              updates.push({
                id,
                change: {
                  sources: Array.from(set).map((l: any) => ({ ref_id: l })),
                },
              })
            }
          })
          condensedMapRisk.forEach((set, id) => {
            if (set && set.size > 0) {
              riskUpdates.push({
                id,
                change: {
                  loss_sets: Array.from(set).map((l: any) => ({ ref_id: l })),
                },
              })
            }
          })
          condensedMapComplex.forEach((set, id) => {
            if (set && set.size > 0) {
              complexUpdates.push({
                id,
                change: {
                  loss_sets: Array.from(set).map((l: any) => ({ ref_id: l })),
                },
              })
            }
          })
          return this.service
            .updatePortfolioLayers(structure.grossPortfolioID, grossLayers)
            .pipe(
              concatMap(() => {
                if (backallocatedUpdates && backallocatedUpdates.length > 0) {
                  return backallocatedUpdates.map((update) => this.service.patchLayer(update))
                } else {
                  return of(null)
                }
              }),
              concatMap(() => {
                if (updates && updates.length > 0) {
                  return this.service.patchLogicalPortfolioLayers(updates)
                } else {
                  return of(null)
                }
              }),
              concatMap(() => {
                if (riskUpdates && riskUpdates.length > 0) {
                  return this.service.patchPhysicalPortfolioLayers(riskUpdates)
                } else {
                  return of(null)
                }
              }),
              concatMap(() => {
                if (sharedUpdates && sharedUpdates.length > 0) {
                  return this.service.patchPhysicalPortfolioLayers(sharedUpdates)
                } else {
                  return of(null)
                }
              }),
              concatMap(() => {
                if (complexUpdates && complexUpdates.length > 0) {
                  return this.service.patchPhysicalPortfolioLayers(complexUpdates)
                } else {
                  return of(null)
                }
              }),
              concatMap(() => {
                return this.service.updatePortfolioLayers(structure.netPortfolioID, netLayers)
              }),
            )
            .pipe(
              map(() => {
                if (isLast) {
                  return swapBulkLossSetsSuccess({
                    id: structure.id,
                    parentGrossPortfolioID: oldParentGrossPortfolioID,
                  })
                } else {
                  return swapBulkLossSetsIntermediateSuccess({
                    id: structure.id,
                  })
                }
              }),
            )
        },
      ),
    )
  })

  showBulkCloneLoadingDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(importBulkLossSets, swapBulkLossSets),
        map(() => {
          this.dialog.open(LoadingPopupComponent, {
            data: {
              title: 'Processing bulk clone',
              subText: 'Please wait for operation to complete...',
            },
            disableClose: true,
            hasBackdrop: true,
          })
        }),
      ),
    { dispatch: false },
  )

  swapBulkLossSetsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(swapBulkLossSetsSuccess, importBulkLossSetsSuccess),
        map(() => {
          this.dialog.closeAll()
          location.reload()
        }),
      ),
    { dispatch: false },
  )

  importBulkLossSets$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(importBulkLossSets),
      concatMap(({ structure, parentGrossPortfolioID, analysisProfileID, isLast }) =>
        this.programService
          .update(structure.id, {
            ...structure,
            libRE: '',
            parentGrossPortfolioID,
            analysisID: analysisProfileID,
          })
          .pipe(
            switchMap(() => {
              return forkJoin([
                this.service.fetchPortfolio(parentGrossPortfolioID),
                this.service.fetchPortfolio(structure.cededPortfolioID),
              ]).pipe(
                switchMap(([lossSetLayers, cededLayers]: [any, any]) => {
                  const riskVisibleLayerRequests = cededLayers.data.layers
                    .filter((layer: any) => layer.meta_data.sage_layer_type === layerIds.noncatRisk)
                    .map((layer: any) => {
                      if (layer.meta_data.program_name === 'Layer 3 combination') {
                        return this.service.fetchLayer(layer.meta_data.riskVisibleLayerID)
                      }
                      return of(null)
                    })
                  return forkJoin(riskVisibleLayerRequests).pipe(
                    map((riskVisibleLayers: any) => {
                      return {
                        structure,
                        parentGrossPortfolioID,
                        lossSetLayers,
                        cededLayers,
                        riskVisibleLayers: riskVisibleLayers.filter((layer: any) => layer !== null),
                        isLast,
                        analysisProfileID,
                      }
                    }),
                    defaultIfEmpty({
                      structure,
                      parentGrossPortfolioID,
                      lossSetLayers,
                      cededLayers,
                      riskVisibleLayers: [],
                      isLast,
                      analysisProfileID,
                    }),
                  )
                }),
                switchMap(
                  ({
                    structure,
                    parentGrossPortfolioID,
                    lossSetLayers,
                    cededLayers,
                    riskVisibleLayers,
                    isLast,
                    analysisProfileID,
                  }) => {
                    return this.handleSharedLimitLayers(
                      cededLayers,
                      lossSetLayers.data.layers,
                      analysisProfileID,
                      this.service,
                    ).pipe(
                      map(({ backallocatedLayers }) => ({
                        structure,
                        parentGrossPortfolioID,
                        lossSetLayers,
                        cededLayers,
                        riskVisibleLayers,
                        backallocatedLayers,
                        isLast,
                        analysisProfileID,
                      })),
                      defaultIfEmpty({
                        structure,
                        parentGrossPortfolioID,
                        lossSetLayers,
                        cededLayers,
                        riskVisibleLayers,
                        backallocatedLayers: [],
                        isLast,
                        analysisProfileID,
                      }),
                    )
                  },
                ),
              )
            }),
          ),
      ),
      switchMap(
        ({
          structure,
          parentGrossPortfolioID,
          lossSetLayers,
          cededLayers,
          riskVisibleLayers,
          isLast,
          analysisProfileID,
          backallocatedLayers,
        }) => {
          const updates: Update<LogicalPortfolioLayer>[] = []
          const backallocatedUpdates: Update<NestedLayer & WithLayerId>[] = []
          const lossRankUpdates: Update<LossSetLayer>[] = []
          const layers: LossSetLayer[] = lossSetLayers.data.layers
          const newLayers: Layer[] = []
          for (const layer of cededLayers.data.layers) {
            if (isLayerShared(layer)) {
              const backallocated = backallocatedLayers.find(
                (bal) =>
                  bal.data.meta_data.backAllocatedForID === layer.meta_data.backAllocatedForID,
              ).data
              const ogLayers = backallocated.sink.sources
              const hiddenIDs: string[] = [...ogLayers.map((ogl: any) => ogl.id)]
              updates.push({
                id: layer.id,
                change: {
                  sources: [{ ref_id: backallocated.id }],
                },
              })
              backallocatedUpdates.push({
                id: backallocated.sink.id,
                change: {
                  meta_data: {
                    ...backallocated.sink.meta_data,
                    analysisProfileID,
                  },
                },
              })
              hiddenIDs.forEach((id) => {
                updates.push({
                  id: id,
                  change: {
                    sources: [...layers.map((l: any) => ({ ref_id: l.id }))],
                  },
                })
              })
            } else if (isLayerActualTopAndDrop(layer)) {
              const sources = lossSetLayers.data.layers
              const hiddenIDs: string[] = []
              layer.sources.forEach((s: any) => {
                if ((s.meta_data.sage_layer_type = layerIds.drop)) {
                  hiddenIDs.push(s.id)
                  if (s.meta_data.topID) {
                    hiddenIDs.push(s.meta_data.topID)
                  }
                }
              })
              layer.sources = layer.sources.map((source: any) => ({
                ...source,
                sources,
              }))
              updates.push(
                ...layer.sources.map((source: any) => ({
                  id: source.id,
                  change: {
                    sources: [...layers.map((l: any) => ({ ref_id: l.id }))],
                  },
                })),
              )
              hiddenIDs.forEach((id: string) => {
                updates.push({
                  id,
                  change: {
                    sources: [...layers.map((l: any) => ({ ref_id: l.id }))],
                  },
                })
              })
            } else if (isLayerAgg(layer) || isLayerAggFeeder(layer)) {
              const newLossSetRefs = layers.map((l: any) => ({ ref_id: l.id }))
              if (isLayerAggFeeder(layer)) {
                // An Aggregate Feeder holds its loss set layers as its OWN direct
                // sources (unlike the aggregate parent, whose direct sources are
                // feeder layers). Repoint the feeder itself to the newly imported
                // loss sets so no stale (old-catalog) loss set stays referenced —
                // otherwise opening the structure fails portfolio_view/layer_view
                // creation with an "incompatible catalog" error.
                updates.push({
                  id: layer.id,
                  change: {
                    sources: [...newLossSetRefs],
                  },
                })
              } else {
                // Aggregate parent: its direct sources are feeder layers. Repoint
                // each feeder's sources to the newly imported loss sets.
                layer.sources.forEach((source: any) => {
                  updates.push({
                    id: source.id,
                    change: {
                      sources: [...newLossSetRefs],
                    },
                  })
                })
              }
            } else if (isSlidingScaleLayer(layer)) {
              const lossSets = lossSetLayers.data.layers
              const nonLAELossSets = lossSets.filter(
                (lossSet: LossSetLayer) => lossSet.meta_data.ls_dim2 !== 'LAE',
              )
              const noLAELossSets = nonLAELossSets.length === lossSets.length
              const laeAdjustment = layer.sources[0].sources.find((l: any) =>
                isSlidingScaleLayer(l, 'lae-adjustment'),
              )
              const getRefs = (source: LossSetLayer[]) =>
                source.map((lossSet) => ({ ref_id: lossSet.id }))
              updates.push({
                id: layer.meta_data.visible_layer_id,
                change: { sources: getRefs(lossSets) },
              })

              const hiddenLayers = [...layer.sources, laeAdjustment]
              const lae = Number(layer.sink.meta_data.laeProvision)
              hiddenLayers.forEach((hiddenLayer) => {
                if (isSlidingScaleLayer(hiddenLayer, 'min-commission')) {
                  updates.push({
                    id: hiddenLayer.id,
                    change: { sources: [...getRefs(nonLAELossSets), { ref_id: laeAdjustment.id }] },
                  })
                } else if (isSlidingScaleLayer(hiddenLayer, 'slide-layer')) {
                  const lossSetsToUse = lae === 0 && !noLAELossSets ? nonLAELossSets : lossSets
                  updates.push({
                    id: hiddenLayer.id,
                    change: { sources: [...getRefs(lossSetsToUse), { ref_id: laeAdjustment.id }] },
                  })
                } else if (isSlidingScaleLayer(hiddenLayer, 'lae-adjustment')) {
                  const laeLossSets = lossSets.filter(
                    (lossSet: LossSetLayer) => lossSet.meta_data.ls_dim2 === 'LAE',
                  )
                  const lossSetsToUse = lae === 0 && !noLAELossSets ? laeLossSets : lossSets
                  updates.push({
                    id: hiddenLayer.id,
                    change: { sources: getRefs(lossSetsToUse) },
                  })
                }
              })
            } else if (
              layer.meta_data.sage_layer_type === layerIds.noncatSwing ||
              layer.meta_data.sage_layer_type === layerIds.ahlSwing ||
              layer.meta_data.sage_layer_type === layerIds.noncatIndxl ||
              isMultiSectionLayer(layer)
            ) {
              const sources = lossSetLayers.data.layers
              const hiddenIDs = [layer.meta_data.visible_layer_id]
              layer.sources.forEach((s: any) => {
                if (
                  layer.meta_data.sage_layer_subtype === 'loss-layer' ||
                  layer.meta_data.sage_layer_subtype === 'adjustment-layer' ||
                  layer.meta_data.sage_layer_subtype === 'premium-layer' ||
                  layer.meta_data.sage_layer_subtype === 'section-layer' ||
                  layer.meta_data.sage_layer_type === 'noncat_indxl'
                ) {
                  hiddenIDs.push(s.id)
                }
              })
              layer.sources = layer.sources.map((source: any) => ({
                ...source,
                sources,
              }))
              updates.push(
                ...layer.sources.map((source: any) => ({
                  id: source.id,
                  change: {
                    sources: [...layers.map((l: any) => ({ ref_id: l.id }))],
                  },
                })),
              )
              hiddenIDs.forEach((id) => {
                updates.push({
                  id,
                  change: {
                    sources: [...layers.map((l: any) => ({ ref_id: l.id }))],
                  },
                })
              })
            } else if (layer.meta_data.sage_layer_type === layerIds.noncatIndxl) {
              const sources = lossSetLayers.data.layers
              const hiddenIDs = [layer.meta_data.visible_layer_id]
              layer.sources.forEach((s: any) => {
                hiddenIDs.push(s.id)
              })
              layer.sources = layer.sources.map((source: any) => ({
                ...source,
                sources,
              }))
              updates.push(
                ...layer.sources.map((source: any) => ({
                  id: source.id,
                  change: {
                    sources: [...layers.map((l: any) => ({ ref_id: l.id }))],
                  },
                })),
              )
              hiddenIDs.forEach((id) => {
                updates.push({
                  id,
                  change: {
                    sources: [...layers.map((l: any) => ({ ref_id: l.id }))],
                  },
                })
              })
            } else if (
              layer.meta_data.sage_layer_type === layerIds.noncatRisk &&
              layer.meta_data.program_name === 'Layer 3 combination'
            ) {
              const sources = lossSetLayers.data.layers.filter(
                (l: any) => l.meta_data.loss_type === 'large' || l.meta_data.loss_type === 'cat',
              )
              const layerToAdd = riskVisibleLayers
                .map((l: any) => l?.data)
                .find((rvl: any) => rvl && rvl.id === layer.meta_data.riskVisibleLayerID)
              if (layerToAdd) {
                layer.physicalLayer = layerToAdd.sink as PhysicalPortfolioLayer

                sources.forEach((l: any) => {
                  const lossSetLayer = {
                    id: l.id,
                    meta_data: l.meta_data,
                    loss_sets: l.loss_sets,
                  }
                  const hiddenLayerMetaData = {
                    ...layerToAdd.meta_data,
                    sage_layer_subtype: 'actual',
                    program_name: l.meta_data.loss_type === 'large' ? 'Layer 2' : 'Layer 1',
                    program_type: 'Riskxl',
                    isRiskLargeHidden: l.meta_data.loss_type === 'large',
                    isRiskVisible: false,
                    isRiskCatHidden: l.meta_data.loss_type === 'cat',
                    isRiskFinal: false,
                    riskActualLayerID: layer.id,
                  }
                  const hiddenLayerPhysicalMetaData = {
                    ...layerToAdd.sink.meta_data,
                    sage_layer_subtype: 'actual',
                    program_name: l.meta_data.loss_type === 'large' ? 'Layer 2' : 'Layer 1',
                    program_type: 'Riskxl',
                    isRiskLargeHidden: l.meta_data.loss_type === 'large',
                    isRiskVisible: false,
                    isRiskCatHidden: l.meta_data.loss_type === 'cat',
                    isRiskFinal: false,
                    riskActualLayerID: layer.id,
                  }
                  const hiddenLayerPhysicalLayer = {
                    ...layerToAdd.sink,
                    participation: Math.abs(layerToAdd.sink.participation),
                    aggregateAttachment: {
                      value: 0,
                      currency: layerToAdd.sink.aggregateAttachment?.currency || 'USD',
                    },
                    aggregateLimit: {
                      value: analyzereConstants.unlimitedValue,
                      currency: layerToAdd.sink.aggregateLimit?.currency || 'USD',
                    },
                    loss_sets: [
                      {
                        id: l.id,
                        meta_data: l.meta_data,
                        loss_sets: l.loss_sets,
                      },
                    ],
                    meta_data: hiddenLayerPhysicalMetaData,
                  }
                  const hiddenLayer: Layer = {
                    ...layerToAdd,
                    lossSetLayers: [lossSetLayer],
                    meta_data: hiddenLayerMetaData,
                    physicalLayer: hiddenLayerPhysicalLayer,
                    sources: [...sources.map((s: any) => ({ ref_id: s.id }))],
                    sink: { ref_id: hiddenLayerPhysicalLayer.id },
                  }
                  newLayers.push(hiddenLayer)
                })
                // Update the Risk Visible layer with the new loss set sources
                updates.push({
                  id: layer.meta_data.riskVisibleLayerID,
                  change: {
                    sources: [...sources.map((s: any) => ({ ref_id: s.id }))],
                  },
                })
              }
            } else if (isLayerFHCF(layer)) {
              // FHCF final layer's sources are the two hidden (LargestTwoLosses /
              // RemainingLosses) layers, each holding a LossRank source that carries the
              // loss sets. Repoint the LossRank loss_sets to the newly imported loss sets;
              // never overwrite the final layer's sources, or the hidden layers can no
              // longer be resolved on reload and the FHCF layer disappears from the tower.
              const fhcfLossSetRefs: Ref[] = layers.flatMap((lossSetLayer) =>
                (lossSetLayer.loss_sets as LossSet[]).map((lossSet) => ({ ref_id: lossSet.id })),
              )
              const hiddenLayers = ((layer as LogicalPortfolioLayer).sources ??
                []) as LogicalPortfolioLayer[]
              for (const hidden of hiddenLayers) {
                const lossRanks = (hidden.sources ?? []) as LossSetLayer[]
                for (const lossRank of lossRanks) {
                  lossRankUpdates.push({
                    id: lossRank.id,
                    change: { loss_sets: fhcfLossSetRefs },
                  })
                }
              }
            } else {
              updates.push({
                id: layer.id,
                change: {
                  sources: [...layers.map((l: any) => ({ ref_id: l.id }))],
                },
              })
            }
          }
          const updatesFinal =
            newLayers.length > 0
              ? this.service.postLossSetLayers(newLayers).pipe(
                  map((newLayerResponses) => {
                    if (newLayerResponses.data) {
                      const layer = cededLayers.data.layers[0]
                      updates.push({
                        id: layer.id,
                        change: {
                          sources: [
                            ...newLayerResponses.data.map((l: any) => ({
                              ref_id: l.id,
                            })),
                          ],
                        },
                      })
                      const rvlToPush = riskVisibleLayers
                        .map((l: any) => l?.data)
                        .find((rvl: any) => rvl && rvl.id === layer.meta_data.riskActualLayerID)
                      if (rvlToPush) {
                        updates.push({
                          id: riskVisibleLayers
                            .map((l: any) => l?.data)
                            .find((rvl: any) => rvl && rvl.id === layer.meta_data.riskActualLayerID)
                            .id,
                          change: {
                            sources: [
                              ...newLayerResponses.data.map((l: any) => ({
                                ref_id: l.id,
                              })),
                            ],
                          },
                        })
                      }
                    }
                    return updates
                  }),
                )
              : of(updates)

          const backallocatedPatches =
            backallocatedUpdates.length > 0
              ? forkJoin(backallocatedUpdates.map((update) => this.service.patchLayer(update)))
              : of([])

          return forkJoin([updatesFinal, backallocatedPatches]).pipe(
            switchMap(([finalUpdates, backallocatedResponses]) => {
              const grossLayers: string[] = layers.map((l: { id: string }) => l.id)
              return this.service
                .updatePortfolioLayers(structure.grossPortfolioID, grossLayers)
                .pipe(
                  // Guard empty updates: patchLogicalPortfolioLayers([]) -> forkJoin([])
                  // completes without emitting, which would stall the chain (e.g. a
                  // pure-FHCF structure whose only patches live in lossRankUpdates).
                  switchMap(() =>
                    finalUpdates.length > 0
                      ? this.service.patchLogicalPortfolioLayers(finalUpdates)
                      : of({ data: [] }),
                  ),
                  switchMap(() =>
                    lossRankUpdates.length > 0
                      ? forkJoin(
                          lossRankUpdates.map((update) => this.service.patchLossSetLayer(update)),
                        )
                      : of([]),
                  ),
                  switchMap(() => {
                    grossLayers.push(...cededLayers.data.layers.map((l: { id: string }) => l.id))
                    return this.service.updatePortfolioLayers(structure.netPortfolioID, grossLayers)
                  }),
                  map(() => {
                    if (isLast) {
                      return importBulkLossSetsSuccess({
                        id: structure.id,
                        parentGrossPortfolioID,
                      })
                    } else {
                      return { type: 'No Action' }
                    }
                  }),
                )
            }),
          )
        },
      ),
    )
  })

  updateProgramIndex$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateProgramIndex),
      mergeMap(({ structure, index }) =>
        this.programService.update(structure.id, {
          ...structure,
          position_index: index,
        }),
      ),
      rejectError((error) => this.store.dispatch(updateProgramIndexFailure({ error }))),
      map(({ id, position_index }) =>
        // tslint:disable-next-line: no-non-null-assertion
        updateProgramIndexSuccess({ id, index: position_index! }),
      ),
    )
  })

  updateProgramIndexes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateProgramIndexes),
      mergeMap(({ req }) => {
        return this.programService.updatePositionIndexes(req)
      }),
      rejectError((error) => this.store.dispatch(updateProgramIndexesFailure({ error }))),
      map((study) => {
        return updateProgramIndexesSuccess({ structures: study })
      }),
    )
  })

  updateProgramFolderID$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateFolderID),
      withLatestFrom(this.store.pipe(select(selectCurrentStudyPrograms))),
      mergeMap(([action, structures]) => {
        const { structureId, folderID } = action
        const structure = structures.filter((s) => {
          return s.id === structureId
        })[0]
        return this.programService.update(structureId, {
          ...structure,
          folderID,
        })
      }),
      rejectError((error) => {
        return this.store.dispatch(updateFolderIDFailure({ error }))
      }),
      map(({ id, folderID }) => {
        return updateFolderIDSuccess({
          structureId: id,
          folderID,
        })
      }),
    )
  })

  updateProgramCounts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateCounts),
      withLatestFrom(
        this.store.pipe(select(selectCurrentStructureID)),
        this.store.pipe(select(selectCurrentStudyPrograms)),
      ),
      mergeMap(([action, structureId, structures]) => {
        const { fotCount, quoteCount } = action
        const structure = structures.filter((s) => {
          return s.id === structureId
        })[0]

        if (structureId) {
          return this.programService.update(structureId, {
            ...structure,
            fotCount,
            quoteCount,
          })
        } else {
          return of([])
        }
      }),
      rejectError((error) => {
        return this.store.dispatch(updateCountsFailure({ error }))
      }),
      map(({ id, fotCount, quoteCount }) => {
        return updateCountsSuccess({
          structureId: id,
          fotCount: fotCount ?? 0,
          quoteCount: quoteCount ?? 0,
        })
      }),
    )
  })

  updateTailMetrics$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateTailMetrics),
      mergeMapWithInput(({ id, tailMetrics }) => {
        const tailMetricsOptions = {
          ...tailMetrics,
          returnPeriodData: [] as ReturnPeriodRow[],
        }
        return this.programService.putTailMetricsOptions(id, tailMetricsOptions)
      }),
      rejectErrorWithInput((error) => this.store.dispatch(updateTailMetricsFailure({ error }))),
      map(([_, { id, tailMetrics }]) => {
        return updateTailMetricsSuccess({ id, tailMetrics })
      }),
    )
  })

  // TODO(flag-cleanup): delete this entire effect when logic-migration.scenario-delete is flipped permanently
  deleteAllLossSetScenarios$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(deleteAllLossSetScenarios),
      withLatestFrom(this.migrationFlags.isEnabled('logic-migration.scenario-delete')),
      filter(([, useNewPath]) => !useNewPath),
      map(([action]) => action),
      withLatestFrom(
        this.store.pipe(select(selectCurrentProgramParentGrossScenarios)),
        this.store.pipe(select(selectCurrentStructureID)),
      ),
      mergeMap(([action, scenarios, structureId]) => {
        if (scenarios.length === 0) {
          return of({ type: 'No Action' })
        }

        // First delete inurance relationships for all scenarios
        const inuranceDeleteOperations = scenarios
          .map((scenario) => {
            if (!action.autoDelete && scenario.parentScenarioID === structureId) {
              return this.inuranceService.deleteScenarioInuranceRelationships(scenario.id).pipe(
                catchError(() => of(undefined)), // Continue even if inurance deletion fails
              )
            } else if (action.autoDelete) {
              return this.inuranceService.deleteScenarioInuranceRelationships(scenario.id).pipe(
                catchError(() => of(undefined)), // Continue even if inurance deletion fails
              )
            } else {
              return of(null)
            }
          })
          .filter((operation) => operation !== null)

        // Use concat instead of forkJoin to process inurance deletions sequentially
        // This prevents race conditions where parallel deletions read stale inuranceSourceFor/inuranceTargetFor
        return concat(...inuranceDeleteOperations).pipe(
          toArray(),
          mergeMap(() => {
            // Then delete the scenarios themselves
            const deleteOperations = scenarios.map((scenario) => {
              if (!action.autoDelete && scenario.parentScenarioID === structureId) {
                return this.programService.delete(scenario.id)
              } else if (action.autoDelete) {
                return this.programService.delete(scenario.id)
              } else {
                return of(null)
              }
            })

            return forkJoin(deleteOperations).pipe(
              map(() => {
                if (action.autoDelete) {
                  this.snackBar.open(
                    'All Loss Set Scenario Structures for the parent gross have been deleted. Close button will refresh the page to update the changes.',
                    'X',
                    {
                      duration: 20000,
                    },
                  )
                } else {
                  this.snackBar.open(
                    'All Loss Set Scenario Structures for the structure have been deleted. Page will refresh to update the changes.',
                    'X',
                    {
                      duration: 20000,
                    },
                  )
                  // Refresh the page when autoDelete is false
                  setTimeout(() => {
                    window.location.reload()
                  }, 500)
                }
                // Dispatch success action after all deletions are complete
                return {
                  type: 'deleteAllLossSetScenariosSuccess',
                }
              }),
              catchError((error) =>
                of({
                  type: 'deleteAllLossSetScenariosFailure',
                  error,
                }),
              ),
            )
          }),
        )
      }),
    )
  })

  deleteAllLossSetScenariosNet$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(deleteAllLossSetScenarios),
      withLatestFrom(this.migrationFlags.isEnabled('logic-migration.scenario-delete')),
      filter(([, useNewPath]) => useNewPath),
      map(([action]) => action),
      withLatestFrom(this.store.pipe(select(selectCurrentStructureID))),
      switchMap(([action, structureId]) => {
        const loadingDialogRef = this.dialog.open(LoadingPopupComponent, {
          data: {
            title: 'Deleting Scenarios',
            subText: 'Please wait for operation to complete...',
          },
          disableClose: true,
          hasBackdrop: true,
        })
        return this.programService
          .deleteAllLossSetScenariosNet(
            structureId ?? '',
            toDeleteAllLossSetScenariosRequest(action.autoDelete),
          )
          .pipe(finalize(() => loadingDialogRef.close()))
      }),
      rejectError((error) => this.store.dispatch(deleteAllLossSetScenariosNetFailure({ error }))),
      map((response) =>
        deleteAllLossSetScenariosNetSuccess(fromDeleteLossSetScenariosResponse(response)),
      ),
    )
  })

  deleteAllLossSetScenariosNetSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(deleteAllLossSetScenariosNetSuccess),
      withLatestFrom(
        this.store.pipe(select(selectCurrentClientID)),
        this.store.pipe(select(selectCurrentStudyID)),
        this.store.pipe(select(selectCurrentYearID)),
        this.store.pipe(select(selectCurrentStructureID)),
      ),
      switchMap(([action, clientID, studyID, yearID, structureID]) => {
        if (action.failedScenarioIds.length > 0) {
          this.snackBar.open(
            `${action.failedScenarioIds.length} Loss Set Scenario Structure(s) could not be deleted.`,
            'X',
            { duration: 20000 },
          )
        } else {
          this.snackBar.open('All Loss Set Scenario Structures have been deleted.', 'X', {
            duration: 20000,
          })
        }

        // Delete-all can fire while a scenario is the routed structure (the
        // scale-loss-set dialog allows it for currentProgram.isScenario, and
        // autoDelete:true spans the whole parent-gross scenario set). Removing
        // the routed entity mid-tick crashes change detection, so navigate to
        // the tier page first — same sequencing as deleteScenarioNetSuccess$.
        const complete = deleteScenarioNetComplete({
          deletedScenarioIds: action.deletedScenarioIds,
        })
        if (structureID && action.deletedScenarioIds.includes(structureID)) {
          const tierPath = {
            client: clientID,
            year: yearID,
            program: studyID,
            structure: structureID,
          }
          return from(this.router.navigate(buildTierRouteCommands(tierPath))).pipe(
            filter((navigated) => navigated),
            map(() => complete),
          )
        }
        return of(complete)
      }),
    )
  })

  // TODO(flag-cleanup): delete this entire effect when logic-migration.scenario-delete is flipped permanently
  deleteScenario$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(deleteScenario),
      withLatestFrom(this.migrationFlags.isEnabled('logic-migration.scenario-delete')),
      filter(([, useNewPath]) => !useNewPath),
      map(([action]) => action),
      withLatestFrom(this.store.pipe(select(selectCurrentProgram))),
      mergeMap(([_, program]) => {
        // First delete inurance relationships for the scenario
        return this.inuranceService.deleteScenarioInuranceRelationships(program.id).pipe(
          mergeMap(() => {
            // Then delete the scenario itself
            return this.programService.delete(program.id)
          }),
          catchError((error) => {
            // If inurance deletion fails, still try to delete the scenario
            return this.programService.delete(program.id)
          }),
        )
      }),
      rejectError((error) => this.store.dispatch(deleteScenarioFailure({ error }))),
      map(() => {
        return deleteScenarioSuccess()
      }),
    )
  })

  deleteScenarioNet$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(deleteScenario),
      withLatestFrom(this.migrationFlags.isEnabled('logic-migration.scenario-delete')),
      filter(([, useNewPath]) => useNewPath),
      map(([action]) => action),
      withLatestFrom(this.store.pipe(select(selectCurrentProgram))),
      switchMap(([_, program]) => {
        const loadingDialogRef = this.dialog.open(LoadingPopupComponent, {
          data: {
            title: 'Deleting Scenario',
            subText: 'Please wait for operation to complete...',
          },
          disableClose: true,
          hasBackdrop: true,
        })
        return this.programService
          .deleteScenarioNet(program?.id ?? '')
          .pipe(finalize(() => loadingDialogRef.close()))
      }),
      rejectError((error) => this.store.dispatch(deleteScenarioFailure({ error }))),
      map((response) => {
        const result = fromDeleteLossSetScenariosResponse(response)
        // The endpoint reports per-scenario business failures with HTTP 200
        // (failedScenarioIds). Mirror the legacy error path for them — no
        // success dialog, no navigation, entity stays in the store.
        return result.failedScenarioIds.length > 0
          ? deleteScenarioFailure({ error: errorPayload('Scenario could not be deleted') })
          : deleteScenarioNetSuccess(result)
      }),
    )
  })

  deleteBulkScenarios$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(deleteBulkScenarios),
      mergeMap(({ structureName, scenarioIds }) => {
        if (scenarioIds.length === 0) {
          return of(deleteBulkItemComplete({ structureName, hadScenarios: false })).pipe(delay(0))
        }

        const inuranceDeleteOps = scenarioIds.map((id) =>
          this.inuranceService
            .deleteScenarioInuranceRelationships(id)
            .pipe(catchError(() => of(undefined))),
        )

        // Use concat instead of forkJoin to process inurance deletions sequentially
        // This prevents race conditions where parallel deletions read stale inuranceSourceFor/inuranceTargetFor
        return concat(...inuranceDeleteOps).pipe(
          toArray(),
          mergeMap(() => {
            const deleteOps = scenarioIds.map((id) =>
              this.programService.delete(id).pipe(catchError(() => of(undefined))),
            )
            return forkJoin(deleteOps).pipe(
              // programService.delete emits failures as {error} values, so
              // surface them on the completion action — callers must not
              // report success for scenarios that still exist (AB#26013).
              map((results) =>
                deleteBulkItemComplete({
                  structureName,
                  hadScenarios: true,
                  error: results.some((r) => !r || !!r.error),
                }),
              ),
            )
          }),
          catchError(() =>
            of(deleteBulkItemComplete({ structureName, hadScenarios: true, error: true })),
          ),
        )
      }),
    )
  })

  // TODO(flag-cleanup): delete this entire effect when logic-migration.scenario-delete is flipped permanently
  deleteScenarioSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(deleteScenarioSuccess),
        withLatestFrom(this.migrationFlags.isEnabled('logic-migration.scenario-delete')),
        filter(([, useNewPath]) => !useNewPath),
        map(([action]) => action),
        withLatestFrom(
          this.store.pipe(select(selectCurrentClientID)),
          this.store.pipe(select(selectCurrentStudyID)),
          this.store.pipe(select(selectCurrentYearID)),
          this.store.pipe(select(selectCurrentStructureID)),
        ),
        switchMap(([_, clientID, studyID, yearID, structureID]) => {
          const dialogRef = this.confirmationDialogService.open({
            message: 'Scenario Deleted Successfully',
            secondaryMessage: 'The page will refresh to update the changes.',
            submitLabel: 'Close',
            hideCancel: true,
          })

          return dialogRef.afterClosed().pipe(
            map(() => {
              const tierPath = {
                client: clientID,
                year: yearID,
                program: studyID,
                structure: structureID,
              }

              this.router.navigate(buildTierRouteCommands(tierPath))
              // Add a small delay before refreshing to allow the store action to dispatch
              setTimeout(() => {
                window.location.reload()
              }, 200)
            }),
          )
        }),
      )
    },
    { dispatch: false },
  )

  deleteScenarioNetSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(deleteScenarioNetSuccess),
      withLatestFrom(
        this.store.pipe(select(selectCurrentClientID)),
        this.store.pipe(select(selectCurrentStudyID)),
        this.store.pipe(select(selectCurrentYearID)),
        this.store.pipe(select(selectCurrentStructureID)),
      ),
      switchMap(([action, clientID, studyID, yearID, structureID]) => {
        const dialogRef = this.confirmationDialogService.open({
          message: 'Scenario Deleted Successfully',
          submitLabel: 'Close',
          hideCancel: true,
        })

        return dialogRef.afterClosed().pipe(
          switchMap(() => {
            const tierPath = {
              client: clientID,
              year: yearID,
              program: studyID,
              structure: structureID,
            }

            // No page reload on the Net path — but the store cleanup must
            // wait until navigation off the deleted scenario's page has
            // finished, or change detection crashes on the missing entity.
            // navigate() resolves false when a guard cancels the navigation;
            // skip the cleanup then — the scenario page would still be routed.
            return from(this.router.navigate(buildTierRouteCommands(tierPath))).pipe(
              filter((navigated) => navigated),
              map(() =>
                deleteScenarioNetComplete({ deletedScenarioIds: action.deletedScenarioIds }),
              ),
            )
          }),
        )
      }),
    )
  })

  handleSharedLimitLayers(
    cededLayers: any,
    lossSetLayers: any[],
    analysisProfileID: string,
    service: AnalyzreService,
  ): Observable<{ backallocatedLayers: any[] }> {
    const sharedLimitLayerRequests = cededLayers.data.layers
      .filter((layer: any) => isLayerShared(layer))
      .map((layer: any) => service.fetchLayer(layer.meta_data.backAllocatedForID))
    return forkJoin(sharedLimitLayerRequests).pipe(
      switchMap((sharedLimitActualLayers: any[]) => {
        const updates = sharedLimitActualLayers.map((layer: any) => ({
          id: layer.data.id,
          change: {
            sources: lossSetLayers.map((l: any) => ({ ref_id: l.id })),
          },
        }))
        const updateReq = service.patchLogicalPortfolioLayers(updates)

        return updateReq.pipe(
          switchMap(() => {
            const layerViewRequests = sharedLimitActualLayers.map((layer) =>
              service.postLayerView(layer.data.id, analysisProfileID),
            )
            return forkJoin(layerViewRequests).pipe(
              map((sharedLimitLayerViews) => ({
                cededLayers,
                sharedLimitLayerViews,
              })),
            )
          }),
          switchMap(({ cededLayers, sharedLimitLayerViews }) => {
            const sharedLimitLayerReqs = cededLayers.data.layers
              .filter((layer: any) => isLayerShared(layer))
              ?.map((layer: any) => {
                const backallocated = layer.sources.find(
                  (ls: any) => ls.meta_data.sage_layer_subtype === 'backallocated',
                )
                const updatedLayerView = sharedLimitLayerViews.filter((view) => {
                  return view.data.layerID === layer.meta_data.backAllocatedForID
                })[0]
                const source_id = updatedLayerView.data.id
                const sink = { ref_id: backallocated.sink.id }
                return this.service.postLayer({
                  ...backallocated,
                  source_id,
                  sink,
                })
              })
            return forkJoin(sharedLimitLayerReqs).pipe(
              map((backallocatedLayers: any[]) => ({
                backallocatedLayers,
              })),
            )
          }),
        )
      }),
    )
  }

  async checkForSharedLimitLayers(
    selectedStructures: Program[],
    allStructures: Program[],
  ): Promise<string[]> {
    const sharedLimitErrorMessageElements: string[] = []
    const cededPortfolios: any[] = []
    try {
      await Promise.all(
        selectedStructures.map(async (ss) => {
          const res = await lastValueFrom(this.service.fetchPortfolio(ss.cededPortfolioID))
          if (!res.error) {
            cededPortfolios.push(res.data)
          }
        }),
      )

      const cededLayers: LogicalPortfolioLayer[] = []
      cededPortfolios.forEach((port) => {
        port.layers.forEach((layer: any) => {
          cededLayers.push(layer as LogicalPortfolioLayer)
        })
      })

      const sharedLimitLayers = cededLayers.filter((layer) => isLayerShared(layer))
      if (sharedLimitLayers?.length) {
        const sharedStructureIDs = new Set<string>()
        const selectedStructureIDs = new Set(selectedStructures.map((s) => s.id))
        sharedLimitLayers.forEach((layer) => {
          layer.sources.forEach((s) => {
            const source = s as any
            source.sink.sources.forEach((ss: any) => {
              const structureID = ss?.meta_data?.structureID
              if (structureID) {
                sharedStructureIDs.add(structureID)
              }
            })
          })
        })

        const missingSharedStructures = Array.from(sharedStructureIDs)
          .filter((id) => !selectedStructureIDs.has(id))
          .map((id) => allStructures.find((s) => s.id === id))

        missingSharedStructures.forEach((structure) => {
          sharedLimitErrorMessageElements.push(`${structure.label}`)
        })
      }
    } catch (error) {
      this.messageService.showMessage('Error fetching portfolio(s)', error)
    }
    return sharedLimitErrorMessageElements
  }

  handleAddLossSets(selectedStructures: Program[], result: CloneDialogResult): Observable<Action> {
    return from(selectedStructures).pipe(
      concatMap((structure, i) => {
        const updatedStructure =
          structure.description === 'No Modeling' ? { ...structure, description: '' } : structure

        const payload = {
          structure: updatedStructure,
          parentGrossPortfolioID: result.parentGrossPortfolioID || '',
          analysisProfileID: result.analysisProfileID || '',
          isLast: i === selectedStructures.length - 1,
        }

        const settled$ = this.actions$.pipe(
          ofType(setProgramNameAndDescriptionSuccess, setProgramNameAndDescriptionFailure),
          filter(
            (action) =>
              action.type === setProgramNameAndDescriptionFailure.type ||
              (action as ReturnType<typeof setProgramNameAndDescriptionSuccess>).id ===
                structure.id,
          ),
          take(1),
          map(() => importBulkLossSets(payload)),
          delay(1200),
        )

        const dispatch$ = defer(() => {
          this.store.dispatch(
            setProgramNameAndDescription({
              structure: updatedStructure,
              name: updatedStructure.label,
              description: updatedStructure.description,
            }),
          )
          return EMPTY
        })

        return merge(settled$, dispatch$)
      }),
    )
  }

  handleSwapLossSets(selectedStructures: Program[], result: CloneDialogResult): Observable<Action> {
    const structure = selectedStructures?.[0]
    if (!structure) {
      return throwError(() => new Error('No structure selected'))
    }

    return forkJoin([
      this.service.fetchPortfolio(structure.parentGrossPortfolioID || ''),
      this.service.fetchPortfolio(result.parentGrossPortfolioID),
    ]).pipe(
      switchMap(([oldLossSets, newLossSets]) => {
        return this.dialog
          .open(SwapLossSetsDialogComponent, {
            data: [oldLossSets, newLossSets],
          })
          .afterClosed()
          .pipe(
            filter(Boolean),
            map((lossSetMapping) =>
              handleSwapBulkLossSets({
                structures: selectedStructures,
                analysisProfileID: result.analysisProfileID || '',
                newParentGrossPortfolioID: result.parentGrossPortfolioID || '',
                lossSetMapping,
              }),
            ),
          )
      }),
    )
  }

  postParentGrossMappingLabels$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postParentGrossMappingLabels),
      debounceTime(50),
      switchMap(({ programId, parentGrossPortfolioId, mapping_labels, scenario_labels }) =>
        this.backendService
          .saveOrUpdateParentGrossMappingLabels(
            programId,
            parentGrossPortfolioId,
            mapping_labels,
            scenario_labels,
          )
          .pipe(
            map(({ data }) => {
              return postParentGrossMappingLabelsSuccess({
                data: {
                  id: data.id,
                  mapping_labels: mapping_labels,
                  scenario_labels: scenario_labels,
                  programId: data.programId,
                  parentGrossPortfolioId: data.parentGrossPortfolioId,
                },
              })
            }),
            catchError((error) => {
              return of(postParentGrossMappingLabelsFailure({ error }))
            }),
          ),
      ),
    ),
  )

  updateParentGrossMappingLabels$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateParentGrossMappingLabels),
      debounceTime(50),
      switchMap(({ programId, parentGrossPortfolioId, mapping_labels, scenario_labels }) =>
        this.backendService
          .saveOrUpdateParentGrossMappingLabels(
            programId,
            parentGrossPortfolioId,
            mapping_labels,
            scenario_labels,
          )
          .pipe(
            map(({ data }) => {
              return updateParentGrossMappingLabelsSuccess({
                data: {
                  id: data.id,
                  mapping_labels: mapping_labels,
                  scenario_labels: scenario_labels,
                  programId: data.programId,
                  parentGrossPortfolioId: data.parentGrossPortfolioId,
                },
              })
            }),
            catchError((error) => {
              return of(updateParentGrossMappingLabelsFailure({ error }))
            }),
          ),
      ),
    ),
  )
}
